import type { AppointmentData, GeneralSettingsData, RoomSettings } from '../../../models';
import type { CalendarSchedule } from '../../CustomDayCalendar';
import { getProfessionalAvailabilitySchedules } from '../../../shared/appointmint/professionalAvailability';
import { parseDateOnlyToLocalDate, parseTimeStringToMinutes } from '../../../shared/utils';
import type { Professional } from './AppointmentForm';

interface DayCalendarScheduleLabels {
    absence: string;
    create: string;
    roomUnavailable: string;
    unknown: string;
    busyByProfessional: (professionalName: string) => string;
}

interface BuildDayCalendarSchedulesParams {
    calendarDateOnly?: string;
    data: AppointmentData;
    dayAppointments: AppointmentData[];
    professionals: Professional[];
    professionalColumnName: string;
    roomColumnName: string;
    generalSettings: GeneralSettingsData;
    selectedBranch?: string;
    userProfessionalId?: string;
    canSeeAppointments: boolean;
    labels: DayCalendarScheduleLabels;
}

const blockedScheduleColor = 'var(--text-muted, #888888)';
const busyByScheduleColor = '#AAAAAA';

const getRoomSettings = (
    generalSettings: GeneralSettingsData,
    roomName: string | undefined,
    selectedBranch: string | undefined,
    branchId: string | undefined,
): RoomSettings | undefined => {
    if (!roomName) return undefined;

    const branchKey = branchId ?? selectedBranch;
    if (branchKey) {
        return generalSettings.branches?.[branchKey]?.rooms?.[roomName] ?? generalSettings.rooms?.[roomName];
    }

    return generalSettings.rooms?.[roomName];
};

const getBlockedRoomSchedules = ({
    calendarDateOnly,
    roomName,
    roomColumnName,
    generalSettings,
    selectedBranch,
    branchId,
    label,
}: {
    calendarDateOnly?: string;
    roomName?: string;
    roomColumnName: string;
    generalSettings: GeneralSettingsData;
    selectedBranch?: string;
    branchId?: string;
    label: string;
}): CalendarSchedule[] => {
    if (!calendarDateOnly || !roomName) return [];

    const roomSettings = getRoomSettings(generalSettings, roomName, selectedBranch, branchId);
    if (!roomSettings?.timeAvailability?.length) return [];

    const date = parseDateOnlyToLocalDate(calendarDateOnly);
    if (!date) return [];

    const openingMinutes = parseTimeStringToMinutes(generalSettings.openningHour);
    const closingMinutes = parseTimeStringToMinutes(generalSettings.closingHour);
    const dayOfWeek = date.getDay();

    const roomAvailability = roomSettings.timeAvailability
        .slice()
        .sort((a, b) => new Date(b.from).getTime() - new Date(a.from).getTime())
        .find(a => new Date(a.from) <= date);

    const availableSlots = (roomAvailability?.timeSlots ?? [])
        .filter(slot => slot.dayOfWeek === dayOfWeek)
        .map(slot => ({
            start: Math.max(parseTimeStringToMinutes(slot.start), openingMinutes),
            end: Math.min(parseTimeStringToMinutes(slot.end), closingMinutes),
        }))
        .filter(slot => slot.start < slot.end)
        .sort((a, b) => a.start - b.start);

    if (!availableSlots.length) {
        return [{
            id: `room-blocked-${roomName}-${calendarDateOnly}-${openingMinutes}-${closingMinutes}`,
            columnName: roomColumnName,
            color: blockedScheduleColor,
            opacity: 0.3,
            startMinutesOfDay: openingMinutes,
            endMinutesOfDay: closingMinutes,
            content: label,
            toolTipContent: label,
            ignoreOverlap: true,
        }];
    }

    const blockedSchedules: CalendarSchedule[] = [];
    let current = openingMinutes;

    for (const slot of availableSlots) {
        if (current < slot.start) {
            blockedSchedules.push({
                id: `room-blocked-${roomName}-${calendarDateOnly}-${current}-${slot.start}`,
                columnName: roomColumnName,
                color: blockedScheduleColor,
                opacity: 0.3,
                startMinutesOfDay: current,
                endMinutesOfDay: slot.start,
                content: label,
                toolTipContent: label,
                ignoreOverlap: true,
            });
        }
        current = Math.max(current, slot.end);
    }

    if (current < closingMinutes) {
        blockedSchedules.push({
            id: `room-blocked-${roomName}-${calendarDateOnly}-${current}-${closingMinutes}`,
            columnName: roomColumnName,
            color: blockedScheduleColor,
            opacity: 0.3,
            startMinutesOfDay: current,
            endMinutesOfDay: closingMinutes,
            content: label,
            toolTipContent: label,
            ignoreOverlap: true,
        });
    }

    return blockedSchedules;
};

const toAppointmentSchedule = (
    appointment: AppointmentData,
    columnName: string,
    content?: string,
    color?: string,
): CalendarSchedule => ({
    id: `${appointment.id}-${columnName}`,
    columnName,
    content: content ?? appointment.clientName ?? appointment.type ?? '',
    color: color ?? appointment.professionalColor ?? 'var(--appGreen)',
    startMinutesOfDay: parseTimeStringToMinutes(appointment.start),
    endMinutesOfDay: parseTimeStringToMinutes(appointment.end),
});

export const buildAppointmentDayCalendarSchedules = ({
    calendarDateOnly,
    data,
    dayAppointments,
    professionals,
    professionalColumnName,
    roomColumnName,
    generalSettings,
    selectedBranch,
    userProfessionalId,
    canSeeAppointments,
    labels,
}: BuildDayCalendarSchedulesParams): CalendarSchedule[] => {
    const professionalAvailabilitySchedules = calendarDateOnly && data.professionalId
        ? (() => {
            const professional = professionals.find(item => item.id === data.professionalId);
            if (!professional) return [];

            return getProfessionalAvailabilitySchedules(
                professional,
                new Date(calendarDateOnly),
                calendarDateOnly,
                professionalColumnName,
                selectedBranch,
                calendarDateOnly,
                labels.absence,
                date => date.getDay(),
            );
        })()
        : [];

    const roomBlockedSchedules = getBlockedRoomSchedules({
        calendarDateOnly,
        roomName: data.room,
        roomColumnName,
        generalSettings,
        selectedBranch,
        branchId: data.branchId,
        label: labels.roomUnavailable,
    });

    const others = dayAppointments.filter(appointment => appointment.id !== data.id);
    const professionalOthers = data.professionalId
        ? others
            .filter(appointment => appointment.professionalId === data.professionalId)
            .map(appointment => toAppointmentSchedule(appointment, professionalColumnName))
        : [];
    const roomOthers = data.room
        ? others
            .filter(appointment => appointment.room === data.room)
            .map(appointment => {
                const shouldHideRoomDetails = !!userProfessionalId
                    && !canSeeAppointments
                    && appointment.professionalId !== userProfessionalId;
                const roomContent = shouldHideRoomDetails
                    ? labels.busyByProfessional(appointment.professionalName || labels.unknown)
                    : undefined;
                const roomColor = shouldHideRoomDetails ? busyByScheduleColor : undefined;
                return toAppointmentSchedule(appointment, roomColumnName, roomContent, roomColor);
            })
        : [];

    const currentStart = parseTimeStringToMinutes(data.start);
    const currentEnd = parseTimeStringToMinutes(data.end);
    const placeholder: CalendarSchedule[] = currentStart && currentEnd
        ? [
            ...(data.professionalId ? [{
                id: `${data.id || 'current'}-prof`,
                columnName: professionalColumnName,
                content: data.clientName || generalSettings.services?.[data.type]?.name || labels.create,
                color: 'var(--appBlue)',
                startMinutesOfDay: currentStart,
                endMinutesOfDay: currentEnd,
            }] : []),
            ...(data.room ? [{
                id: `${data.id || 'current'}-room`,
                columnName: roomColumnName,
                content: data.clientName || generalSettings.services?.[data.type]?.name || labels.create,
                color: 'var(--appBlue)',
                startMinutesOfDay: currentStart,
                endMinutesOfDay: currentEnd,
            }] : []),
        ]
        : [];

    return [
        ...professionalAvailabilitySchedules,
        ...roomBlockedSchedules,
        ...professionalOthers,
        ...roomOthers,
        ...placeholder,
    ];
};
